import { BrandMark } from '@argink/ui';
import { CeremonyEntryPage } from './pages/CeremonyEntryPage';
import { SignPage } from './pages/SignPage';
import { StateCard } from './components/StateCard';
import { setSessionToken } from './lib/api';
import { parseCeremonyLinkRoute } from './lib/ceremonyLink';

/**
 * Roteamento mínimo — o produto tem DUAS rotas reais, então continuamos sem puxar react-router
 * (SPA leve). Se o produto ganhar mais telas depois, essa é a hora de trocar por um roteador de
 * verdade.
 *
 * 1. `/sign?c=<selector>.<verifier>` (I2) — a rota que o E-MAIL do signatário aponta. O `c` é um
 *    ponteiro opaco de uso único, trocado por um SST em `POST /web/sign/resolve`. É o caminho do
 *    usuário final, e o único que pode ir num e-mail: nada aqui vale por si mesmo.
 *
 * 2. `/sign/:signerId?envelope=:envelopeId&t=:token` — a rota histórica, onde `t` é o SST CRU na
 *    URL. Serve ao `signing-workbench` e a diagnóstico, que já trabalham com o token em mãos.
 *    NÃO serve para e-mail (canal encaminhável e armazenado) — e, desde U2-001, só é reconhecida
 *    em build DEV: em produção o app nem tenta ler o `t`, só o ceremony_link `?c=` existe.
 */
type Route =
  | { kind: 'ceremony_link'; c: string }
  | { kind: 'direct_token'; signerId: string; envelopeId: string; token: string };

/**
 * U1-001 — tira o `t=<SST>` da barra de endereço assim que ele é lido, ANTES de qualquer
 * validação do resto da rota (U2-001: uma rota malformada — sem `envelope`, por exemplo — não
 * pode deixar o token para trás só porque a rota inteira acabou rejeitada). O token (TTL 60min)
 * exposto na URL vaza por histórico, screenshots, logs de proxy e Referer durante toda a
 * cerimônia. `envelope` fica: `envelopeId` não é segredo, só o `t` é. Mesma técnica de
 * `replaceCeremonyLinkInUrl` (ceremonyLink.ts) — `history.replaceState`, sem navegar e sem
 * empilhar histórico.
 */
function clearDirectTokenFromUrl(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.delete('t');
  window.history.replaceState(null, '', `${url.pathname}${url.search}`);
}

function parseRoute(pathname: string, search: string): Route | null {
  const link = parseCeremonyLinkRoute(pathname, search);
  if (link) return { kind: 'ceremony_link', c: link.c };

  // U2-001: a rota legada `t=` só é reconhecida em build DEV (workbench/diagnóstico — Fase 8:
  // falta prova de compatibilidade para remover o código de vez). Em PRODUÇÃO,
  // `import.meta.env.DEV` vira `false` estaticamente em `vite build`, e o Vite elimina este
  // ramo inteiro do bundle por dead-code elimination — mesmo padrão do guard de mocks em
  // apps/web/src/main.tsx. Antes disto (U1-001 isolado), o browser já mandava `?t=` ao
  // servidor/proxy no PRIMEIRO request de produção, antes de qualquer JS rodar; agora o
  // caminho para o token virar sessão nem existe no bundle de produção — nunca é lido.
  if (!import.meta.env.DEV) return null;

  const match = /^\/sign\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;
  const params = new URLSearchParams(search);
  const token = params.get('t');
  // U2-001: risca o `t` da URL JÁ AQUI, antes de checar `signerId`/`envelope` — para que uma
  // rota malformada (ex.: só `t=`, sem `envelope`) também não deixe o SST na barra de endereço.
  if (token !== null) clearDirectTokenFromUrl();

  const signerId = decodeURIComponent(match[1] ?? '');
  const envelopeId = params.get('envelope');
  if (!signerId || !envelopeId || !token) return null;
  return { kind: 'direct_token', signerId, envelopeId, token };
}

/**
 * O parse é do BOOTSTRAP: uma vez por carga de página, nunca por render.
 *
 * `parseRoute` tem efeito colateral deliberado — risca o `t=<SST>` da barra de endereço assim que
 * o lê (U1-001/U2-001). Ler duas vezes, portanto, não é idempotente: a segunda leitura encontra a
 * URL já limpa e conclui "link inválido". E o App RENDERIZA duas vezes em desenvolvimento, porque
 * `main.tsx` o envolve em `React.StrictMode` — o que derrubava a rota de diagnóstico `t=` numa tela
 * de erro, com um SST perfeitamente válido em mãos. Memorizar aqui preserva a propriedade que
 * U1-001 comprou (o token sai da URL na primeira leitura) sem que a segunda render a destrua.
 */
let bootstrapRoute: { value: Route | null } | null = null;

function bootstrapParseRoute(): Route | null {
  bootstrapRoute ??= { value: parseRoute(window.location.pathname, window.location.search) };
  return bootstrapRoute.value;
}

/** Só para teste: cada caso monta um `window` próprio e precisa de um bootstrap limpo. */
export function __resetBootstrapRouteForTests(): void {
  bootstrapRoute = null;
}

export function App() {
  const route = bootstrapParseRoute();

  if (route?.kind === 'direct_token') {
    // SST em memória, nunca localStorage/sessionStorage (contrato 1 do ledger) — definido
    // uma vez aqui, no bootstrap do app (sem navegação client-side depois).
    setSessionToken(route.token);
    // U1-001/U2-001: a URL já foi limpa dentro de parseRoute, antes mesmo de validar a rota.
    return <SignPage signerId={route.signerId} envelopeId={route.envelopeId} />;
  }

  if (route?.kind === 'ceremony_link') {
    // A resolução é assíncrona (ida ao servidor), então a tela de entrada é quem monta a
    // SignPage depois de receber o SST — ver CeremonyEntryPage.
    return <CeremonyEntryPage c={route.c} />;
  }

  return (
    <main className="page">
      <header className="page__header">
        <BrandMark variant="wordmark" className="page__brand" />
        <h1 className="page__title">Link de assinatura inválido</h1>
      </header>
      <StateCard
        tone="error"
        title="Não encontramos um documento para assinar neste link"
        detail="Confira se copiou o endereço inteiro que você recebeu por e-mail — ele costuma ser longo e pode ter sido cortado pelo aplicativo de mensagens."
      >
        <p className="state-card__hint mono">Exemplo: /sign?c=A1b2...C3d4</p>
      </StateCard>
    </main>
  );
}
